import { useTranslations } from '@vocab/react';
import {
  Box,
  BraidPortal,
  IconTick,
  IconMinus,
  Stack,
  Text,
  IconInfo,
} from 'braid-design-system';
import type React from 'react';
import { type ReactNode, useContext, useEffect } from 'react';
import { usePopperTooltip } from 'react-popper-tooltip';

import translations from '@seek/rr-caja/src/.vocab';

import { ToolTipName } from '../../analytics/types';
import { ForwardedBlankButton } from '../BlankButton/BlankButton';
import { IconSeekPassInvert } from '../Icons';
import { AnalyticsContext } from '../RoleRequirements/RoleRequirements';

import * as style from './ContextualHelp.css';

interface ContextualHelpProps {
  showCertsyTooltip: boolean;
}

const IconText: React.FunctionComponent<{
  icon: ReactNode;
  iconSize?: 'xsmall' | 'small' | 'large' | 'standard';
  text: string;
}> = ({ icon, text, iconSize }) => (
  <Box display="flex">
    <Box className={style.iconWidthText}>
      <Text size={iconSize ?? 'xsmall'}>{icon}</Text>
    </Box>
    <Text size="small">{text}</Text>
  </Box>
);

const ContextualHelp: React.FunctionComponent<ContextualHelpProps> = ({
  showCertsyTooltip,
}) => {
  const analytics = useContext(AnalyticsContext);
  const { t } = useTranslations(translations);

  const {
    getArrowProps,
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible,
  } = usePopperTooltip({
    offset: [0, 12],
    placement: 'bottom',
  });

  useEffect(() => {
    if (visible) {
      analytics.sendContextualHelpLinkSelectedEvent(ToolTipName.ContextualHelp);
    }
  }, [visible, analytics]);

  return (
    <div aria-haspopup="dialog">
      <Box>
        <ForwardedBlankButton
          aria-label={t('aria-label:help-information')}
          ref={setTriggerRef}
        >
          <Text size="standard">
            <IconInfo tone="secondary" />
          </Text>
        </ForwardedBlankButton>
        {visible && (
          <BraidPortal>
            <Box
              aria-modal={true}
              role="dialog"
              boxShadow="large"
              background="neutral"
              borderRadius="standard"
              maxWidth="xsmall"
              zIndex={2}
              ref={setTooltipRef}
              {...getTooltipProps()}
            >
              <Box padding="medium">
                <Stack space="medium">
                  <Text weight="medium" size="standard">
                    {t('contextualHelp.certsy.title')}
                  </Text>
                  <Text size="small">{t('contextualHelp.certsy.body1')}</Text>
                  <Text size="small">{t('contextualHelp.certsy.body2')}</Text>
                  <Text size="small">{t('contextualHelp.certsy.body3')}</Text>
                  <Stack space="medium">
                    {showCertsyTooltip && (
                      <IconText
                        icon={<IconSeekPassInvert />}
                        text={t('contextualHelp.certsy.verify')}
                      />
                    )}
                    <IconText
                      iconSize="small"
                      icon={<IconTick />}
                      text={t('contextualHelp.match')}
                    />
                    <IconText
                      iconSize="small"
                      icon={<IconMinus />}
                      text={t('contextualHelp.noMatch')}
                    />
                  </Stack>
                </Stack>
              </Box>
              <Box
                {...getArrowProps()}
                borderRadius="standard"
                background="neutral"
                className={style.arrow}
              />
            </Box>
          </BraidPortal>
        )}
      </Box>
    </div>
  );
};

export default ContextualHelp;
